import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "absuk:cookie-consent";

/**
 * Lightweight cookie consent banner. We only run essential cookies (session
 * auth) plus opt-in localStorage for translation cache / meditation streaks.
 * Banner sits at the bottom and persists the user's choice (accepted or
 * rejected) in localStorage so it doesn't re-appear on each navigation.
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (!v) setVisible(true);
    } catch (_) {
      // localStorage blocked — show banner anyway, decision won't persist
      setVisible(true);
    }
  }, []);

  const decide = (choice) => {
    try {
      localStorage.setItem(STORAGE_KEY, choice);
    } catch (_) {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      className="fixed bottom-3 left-3 right-3 sm:left-6 sm:right-auto sm:bottom-6 sm:max-w-md z-[1000]"
    >
      <div className="bg-navy text-pearl border border-saffron/40 rounded-sm shadow-2xl p-5">
        <div className="flex items-start gap-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-saffron/15 text-saffron border border-saffron/30 shrink-0">
            <Cookie size={16} />
          </span>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-base text-pearl">Cookies on ABGN-UK</h3>
            <p className="mt-1 text-xs text-pearl/75 leading-relaxed">
              We use essential cookies to keep you signed in and a small amount
              of browser storage to remember your language and meditation
              streaks. We do not use advertising or third-party tracking
              cookies. See our{" "}
              <Link to="/privacy" className="underline text-saffron">
                Privacy notice
              </Link>{" "}
              for full details.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => decide("accepted")}
                data-testid="cookie-accept"
                className="px-4 py-2 bg-saffron text-navy rounded-sm text-xs font-semibold hover:bg-saffron-light"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => decide("rejected")}
                data-testid="cookie-reject"
                className="px-4 py-2 bg-transparent border border-pearl/30 text-pearl rounded-sm text-xs hover:bg-pearl/10"
              >
                Reject non-essential
              </button>
              <Link
                to="/privacy"
                className="px-4 py-2 text-xs text-pearl/80 hover:text-saffron underline"
                data-testid="cookie-learn-more"
              >
                Learn more
              </Link>
            </div>
          </div>
          <button
            type="button"
            onClick={() => decide("dismissed")}
            aria-label="Dismiss cookie banner"
            data-testid="cookie-dismiss"
            className="text-pearl/50 hover:text-pearl shrink-0"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
